import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getCompany } from 'src/api/company';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { Spinner } from '@repo/components';
import StatusError from 'src/components/StatusError';
import GeneralStatusError from 'src/components/StatusError/General';

export const Route = createFileRoute('/company_/blocked/$groupId')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		reason: (search.reason as string) || '',
	}),
});

function RouteComponent() {
	const { groupId } = Route.useParams();
	const { reason } = Route.useSearch();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();

	const { data: group, isLoading: groupLoading } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	if (userLoading || groupLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!group || !authenticatedUser) {
		return null;
	}

	return (
		<GeneralStatusError
			label="Access Blocked"
			action={{
				label: 'Go back to company overview',
				onClick: () => {
					navigate({ to: '/company' });
				},
			}}
		>
			<StatusError.Section>
				<StatusError.Section.Title>Why is access blocked?</StatusError.Section.Title>
				<StatusError.Section.Text>
					{reason ||
						'Your company has a required task that needs to be completed before you can access the company portal. This task must be completed by a user with the appropriate permissions.'}
				</StatusError.Section.Text>
			</StatusError.Section>

			<StatusError.Section>
				<StatusError.Section.Title>What can you do?</StatusError.Section.Title>
				<StatusError.Section.Text>
					Please contact a company administrator to complete the required task. Once the task is completed, you will
					be able to access the company portal.
				</StatusError.Section.Text>
			</StatusError.Section>
		</GeneralStatusError>
	);
}
